import { formatNumber } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Hectometre } from '../../../../classes/hectometre';
import { FormattedAddress, LocationService } from '../../../../services/location.service';
import { InputComponent } from '../input/input.component';

export type LocationResultType = 'address' | 'hectometre';

export interface LocationResult {
    type: LocationResultType;
    label: string;
    sublabel?: string;
    lat: number;
    lng: number;
    address?: FormattedAddress;
    hectometre?: Hectometre;
}

@Component({
    selector: 'app-location-input',
    standalone: false,
    templateUrl: './location-input.component.html',
    styleUrl: './location-input.component.scss',
})
export class LocationInputComponent extends InputComponent implements OnInit {
    @ViewChild('locationSelect') locationSelect?: NgSelectComponent;

    private locationService = inject(LocationService);
    locationInput$ = new Subject<string>();
    locations: LocationResult[] = [];
    loading = false;
    currentSearchTerm = '';
    minTermLength = 3;
    typeToSearchText = `Typ minimaal ${this.minTermLength} karakters om te zoeken`;

    ngOnInit(): void {
        this.showDefaultLocations();

        this.subscriptions.add(
            combineLatest([
                this.locationService.searchLocations(this.locationInput$, 'LocationInputComponent').pipe(
                    catchError(() => of([])),
                    map(addresses => addresses.filter(address => address.locality || address.town))
                ),
                this.locationService
                    .searchHecto(this.locationInput$, this.minTermLength)
                    .pipe(catchError(() => of([]))),
            ])
                .pipe(
                    map(([addresses, hectometres]) => [
                        ...this.mapAddressesToResults(addresses),
                        ...this.mapHectometresToResults(hectometres),
                    ])
                )
                .subscribe(results => {
                    this.loading = false;
                    this.locations = this.includeSelectedLocationIfNeeded(results);
                })
        );
    }

    onSearch(term: string): void {
        const search = (term ?? '').trim();

        this.currentSearchTerm = search;
        this.loading = search.length >= this.minTermLength;

        this.locationInput$.next(search);
    }

    private mapAddressesToResults(addresses: FormattedAddress[]): LocationResult[] {
        return addresses.map(address => ({
            type: 'address',
            label: address.formattedAddress,
            lat: address.lat,
            lng: address.lng,
            address,
        }));
    }

    private mapHectometresToResults(hectometres: Hectometre[]): LocationResult[] {
        return hectometres.map(hecto => ({
            type: 'hectometre',
            label: `${hecto.waynumber} - ${formatNumber(hecto.hectometre / 10, 'nl', '0.1-3')}${hecto.letter ?? ''}`,
            sublabel: [hecto.street, hecto.place].filter(Boolean).join(', '),
            lat: hecto.lat,
            lng: hecto.lng,
            hectometre: hecto,
        }));
    }

    onSelection(event: LocationResult) {
        if (!event) {
            return;
        }

        if (this.fc.parent.get('location_data')) {
            this.fc.parent.get('location_data').setValue(event.address ?? null);
        }

        if (this.fc.parent.get('lat') && this.fc.parent.get('lng')) {
            this.fc.parent.get('lat').setValue(event.lat);
            this.fc.parent.get('lng').setValue(event.lng);
        } else {
            console.error('lat and lng fields not found');
        }
    }

    trackByFn(item: LocationResult) {
        return `${item.type}-${item.label}`;
    }

    showDefaultLocations(openDropdown = false): void {
        if (this.currentSearchTerm) {
            return;
        }

        this.loading = false;
        this.locations = this.fc?.value
            ? this.includeSelectedLocationIfNeeded([])
            : this.mapAddressesToResults(this.locationService.getDefaultLocations());

        if (openDropdown) {
            setTimeout(() => this.locationSelect?.open());
        }
    }

    private includeSelectedLocationIfNeeded(results: LocationResult[]): LocationResult[] {
        const selectedLocation = this.fc?.value;

        if (!selectedLocation || results.some(location => location.label === selectedLocation)) {
            return results;
        }

        return [
            {
                type: 'address',
                label: selectedLocation,
                lat: null,
                lng: null,
                address: { formattedAddress: selectedLocation } as FormattedAddress,
            },
            ...results,
        ];
    }
}
